import type { FC, ReactNode } from 'react';
import { createInstance, OptimizelyProvider } from '@optimizely/react-sdk';

import commonConfig from '../../configs/commonConfig';
import useUrlInfo from '../../utils/urls/useUrlInfo';

import useAppConfig from 'canvas_states/config/useAppConfig';
import useGetCurrentScreenType from '../../utils/screenSizes/useGetCurrentScreenType';

export interface FeatureFlagsProviderProps {
  children: ReactNode;
}

const optimizelyClient = commonConfig.optimizelyApiKey
  ? createInstance({
      sdkKey: commonConfig.optimizelyApiKey,
    })
  : undefined;
/**
 * Instatiates the `optimizely` client and passes `pid` and `partnerConfig` as `user` `attributes`.
 *
 * Feature flags are then accessible via the hook `useRollout`.
 *
 * @see https://plusgrade.jira.com/wiki/spaces/FRON/pages/3740532808/
 */
const FeatureFlagsProvider: FC<FeatureFlagsProviderProps> = ({ children }) => {
  const { pid, requestId, addChannelSource, overrideChannelSource } = useUrlInfo().states;
  const partnerConfig = useAppConfig().states.config;
  const { currentScreenType } = useGetCurrentScreenType().states;
  if (!partnerConfig) return null;
  if (!optimizelyClient) return children;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attributes: any = {
    pid,
    pax: partnerConfig.pax,
    channelSource: addChannelSource ?? overrideChannelSource,
    partnerType: partnerConfig.partnerType,
    labsPartnerType: partnerConfig.labsPartnerType,
    language: partnerConfig.language,
    defaultLanguage: partnerConfig.defaultLanguage,
    supportedLanguages: partnerConfig.supportedLanguages,
    defaultCurrency: partnerConfig.defaultCurrency,
    supportedCurrencies: partnerConfig.supportedCurrencies,
    currentScreenType,
  };

  return (
    <OptimizelyProvider optimizely={optimizelyClient} timeout={500} user={{ id: requestId, attributes }}>
      {children}
    </OptimizelyProvider>
  );
};

export default FeatureFlagsProvider;
